import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getMeasurements,
  createMeasurement,
  updateMeasurement,
  softDeleteMeasurement,
} from "../../firebase";

/**
 * Body measurements (מדידות) — nutrition clinics only.
 *
 * Modelled on punchCardsApi: one tag per patient, so adding a measurement
 * refreshes that patient's list and nothing else.
 */
export const measurementsApi = createApi({
  reducerPath: "measurementsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Measurements"],
  endpoints: (builder) => ({
    getMeasurements: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const measurements = await getMeasurements(clinicId, patientId);
          return { data: measurements };
        } catch (error) {
          console.error("[CK-7812] Error fetching measurements:", {
            errorCode: "CK-7812",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "Measurements", id: `PATIENT-${patientId}` },
      ],
    }),

    createMeasurement: builder.mutation({
      async queryFn({ clinicId, patientId, data, therapistId, therapistName }) {
        try {
          const measurement = await createMeasurement(clinicId, patientId, data, {
            therapistId,
            therapistName,
          });
          return { data: measurement };
        } catch (error) {
          console.error("[CK-7813] Error creating measurement:", {
            errorCode: "CK-7813",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Measurements", id: `PATIENT-${patientId}` },
      ],
    }),

    updateMeasurement: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        measurementId,
        data,
        therapistId,
        therapistName,
      }) {
        try {
          const measurement = await updateMeasurement(
            clinicId,
            patientId,
            measurementId,
            data,
            { therapistId, therapistName }
          );
          return { data: measurement };
        } catch (error) {
          console.error("[CK-7814] Error updating measurement:", {
            errorCode: "CK-7814",
            errorMessage: error.message,
            clinicId,
            patientId,
            measurementId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Measurements", id: `PATIENT-${patientId}` },
      ],
    }),

    // Soft delete — there is no hard delete and no restore
    deleteMeasurement: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        measurementId,
        reason,
        therapistId,
        therapistName,
      }) {
        try {
          const result = await softDeleteMeasurement(clinicId, patientId, measurementId, {
            therapistId,
            therapistName,
            reason,
          });
          return { data: result };
        } catch (error) {
          console.error("[CK-7815] Error deleting measurement:", {
            errorCode: "CK-7815",
            errorMessage: error.message,
            clinicId,
            patientId,
            measurementId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Measurements", id: `PATIENT-${patientId}` },
      ],
    }),
  }),
});

export const {
  useGetMeasurementsQuery,
  useCreateMeasurementMutation,
  useUpdateMeasurementMutation,
  useDeleteMeasurementMutation,
} = measurementsApi;
